import type { ChoreInput } from "@/lib/schemas";

/**
 * Preset chore = wszystkie pola ChoreInput minus te które ustalamy dynamicznie
 * przy create dla konkretnego dziecka:
 * - `assignedTo` — uid nowo utworzonego dziecka (znane dopiero po addChild)
 * - `fundedBy`   — opcjonalne, brak = createdBy (backward-compat), więc pomijamy
 * - `active`     — zawsze true przy create
 */
export type ChorePresetChore = Omit<ChoreInput, "assignedTo" | "fundedBy" | "active">;

export type ChorePresetId =
  | "preset_6_8"
  | "preset_9_11"
  | "preset_12_14"
  | "preset_offline";

export type ChorePreset = {
  id: ChorePresetId;
  label: string;
  description: string;
  emoji: string;
  recommendedAgeMin: number;
  recommendedAgeMax: number;
  chores: ChorePresetChore[];
};

const preset_6_8: ChorePreset = {
  id: "preset_6_8",
  label: "Zestaw dla 6-8 latka",
  description:
    "Krótkie, codzienne rytuały które uczą samodzielności. Bez presji, bez pieniędzy.",
  emoji: "🧒",
  recommendedAgeMin: 6,
  recommendedAgeMax: 8,
  chores: [
    {
      title: "Posłane łóżko rano",
      icon: "🛏️",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "S",
      schedule: { type: "daily" },
      requiresApproval: true,
    },
    {
      title: "Spakowany plecak na jutro",
      icon: "🎒",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "S",
      schedule: { type: "daily" },
      requiresApproval: true,
    },
    {
      title: "Karmienie zwierzaka",
      icon: "🐾",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "S",
      schedule: { type: "daily" },
      requiresApproval: true,
    },
    {
      title: "Sprzątnięte zabawki wieczorem",
      icon: "🧸",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "S",
      schedule: { type: "daily" },
      requiresApproval: true,
    },
    {
      title: "Pomoc przy nakrywaniu do stołu",
      icon: "🍽️",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "S",
      schedule: { type: "daily" },
      requiresApproval: true,
    },
    {
      title: "Mycie auta z rodzicem",
      icon: "🚗",
      rewardMinor: 500,
      kind: "extra",
      offline: true,
      size: "M",
      schedule: { type: "weekly", daysOfWeek: [6] },
      requiresApproval: true,
    },
  ],
};

const preset_9_11: ChorePreset = {
  id: "preset_9_11",
  label: "Zestaw dla 9-11 latka",
  description:
    "Codzienne obowiązki domowe + 1-2 płatne zadania weekendowe. Wzmacniają sprawczość.",
  emoji: "👦",
  recommendedAgeMin: 9,
  recommendedAgeMax: 11,
  chores: [
    {
      title: "Posłane łóżko rano",
      icon: "🛏️",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "S",
      schedule: { type: "daily" },
      requiresApproval: true,
    },
    {
      title: "Rozładowanie zmywarki",
      icon: "🍽️",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "S",
      schedule: { type: "daily" },
      requiresApproval: true,
    },
    {
      title: "Wyniesione śmieci",
      icon: "🗑️",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "S",
      schedule: { type: "daily" },
      requiresApproval: true,
    },
    {
      title: "Spakowany plecak wieczorem",
      icon: "🎒",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "S",
      schedule: { type: "daily" },
      requiresApproval: true,
    },
    {
      title: "Mycie okien",
      icon: "🪟",
      rewardMinor: 800,
      kind: "extra",
      offline: true,
      size: "M",
      schedule: { type: "weekly", daysOfWeek: [6] },
      requiresApproval: true,
    },
    {
      title: "Grabienie liści w ogrodzie",
      icon: "🍂",
      rewardMinor: 1000,
      kind: "extra",
      offline: true,
      size: "M",
      schedule: { type: "weekly", daysOfWeek: [6] },
      requiresApproval: true,
    },
  ],
};

const preset_12_14: ChorePreset = {
  id: "preset_12_14",
  label: "Zestaw dla 12-14 latka",
  description:
    "Większa odpowiedzialność i większe stawki. Pierwsze prawdziwe „kieszonkowe za pracę”.",
  emoji: "🧑",
  recommendedAgeMin: 12,
  recommendedAgeMax: 14,
  chores: [
    {
      title: "Pranie własnych rzeczy",
      icon: "👕",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "M",
      schedule: { type: "weekly", daysOfWeek: [3] },
      requiresApproval: true,
    },
    {
      title: "Zakupy z listą",
      icon: "🛒",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "M",
      schedule: { type: "weekly", daysOfWeek: [6] },
      requiresApproval: true,
    },
    {
      title: "Sprzątnięty pokój",
      icon: "🧹",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "M",
      schedule: { type: "weekly", daysOfWeek: [6] },
      requiresApproval: true,
    },
    {
      title: "Koszenie trawy",
      icon: "🌱",
      rewardMinor: 1500,
      kind: "extra",
      offline: true,
      size: "L",
      schedule: { type: "weekly", daysOfWeek: [6] },
      requiresApproval: true,
    },
    {
      title: "Pomoc w gotowaniu obiadu",
      icon: "👨‍🍳",
      rewardMinor: 800,
      kind: "extra",
      offline: true,
      size: "M",
      schedule: { type: "weekly", daysOfWeek: [0, 4] },
      requiresApproval: true,
    },
    {
      title: "Opieka nad młodszym rodzeństwem (1h)",
      icon: "👶",
      rewardMinor: 1000,
      kind: "extra",
      offline: true,
      size: "M",
      schedule: { type: "weekly", daysOfWeek: [5] },
      requiresApproval: true,
    },
  ],
};

const preset_offline: ChorePreset = {
  id: "preset_offline",
  label: "Czas poza ekranem 🌳",
  description:
    "Uniwersalny zestaw — sport, książka, kontakt z rodziną. Sianko promuje świat poza telefonem.",
  emoji: "🌳",
  recommendedAgeMin: 6,
  recommendedAgeMax: 14,
  chores: [
    {
      title: "Spacer z psem",
      icon: "🐕",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "S",
      schedule: { type: "daily" },
      requiresApproval: true,
    },
    {
      title: "Czytanie książki 20 minut",
      icon: "📖",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "S",
      schedule: { type: "daily" },
      requiresApproval: true,
    },
    {
      title: "Gra planszowa z rodziną",
      icon: "🎲",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "M",
      schedule: { type: "weekly", daysOfWeek: [0] },
      requiresApproval: true,
    },
    {
      title: "Sport na powietrzu",
      icon: "🏃",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "M",
      schedule: { type: "weekly", daysOfWeek: [1, 3, 5] },
      requiresApproval: true,
    },
    {
      title: "Rysowanie lub majsterkowanie",
      icon: "🎨",
      rewardMinor: 0,
      kind: "family",
      offline: true,
      size: "S",
      schedule: { type: "weekly", daysOfWeek: [2, 4] },
      requiresApproval: true,
    },
  ],
};

export const CHORE_PRESETS: ChorePreset[] = [
  preset_6_8,
  preset_9_11,
  preset_12_14,
  preset_offline,
];

/**
 * Wybiera preset wiekowy najlepiej dopasowany do wieku dziecka.
 * Zwraca `preset_12_14` dla wieku 14+ (najstarsza grupa).
 * Nie zwraca `preset_offline` — to suplement, nie default.
 */
export function recommendPresetForAge(age: number): ChorePresetId {
  if (age <= 8) return "preset_6_8";
  if (age <= 11) return "preset_9_11";
  return "preset_12_14";
}

export function getPresetById(id: ChorePresetId): ChorePreset | undefined {
  return CHORE_PRESETS.find((p) => p.id === id);
}
